import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useHeaderFieldStyles } from "./css";
import { HeaderMenu } from "../index";
import { moduleType } from "../../utils/modules";
import {
  userExpandRows,
  uiLanguage,
  userClientDb,
} from "../../modules/auth/selectors/userSelector";
import { SimpleMarkdown } from "../../utils/markdown";
import { Skeleton, useTheme } from "@mui/material";
import { useLoader } from "../../hooks";
import { AttachFile } from "@mui/icons-material";

interface ICellRenderer {
  data: {
    operationCode: string;
    stateIndicator?: string;
    element1?: string;
    element2?: string;
    appPositions?: number;
    documentPositions?: number;
    operationType?: string;
    fileCount?: number | string;
  };
  link?: string;
  linkId?: "operationCode";
  headerColor?: "grey" | "noColor";
  module?: moduleType | "";
}

function HeaderField(props: ICellRenderer) {
  const { data, link = "", linkId, headerColor = "noColor", module } = props;
  const { classes, cx } = useHeaderFieldStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const expandRows = useSelector(userExpandRows);
  const theme = useTheme();
  const locale = useSelector(uiLanguage);
  const userDb = useSelector(userClientDb);
  const userDbId = userDb ? userDb.id : 0;

  const appState = data && data.stateIndicator ? data.stateIndicator : "N";
  let opTip =
    (data && data.operationType && data.operationType.toUpperCase())?.charAt(
      0,
    ) || "";
  const linkTo = linkId && data ? `${link}/${data[linkId]}` : link;
  const [loading] = useLoader();

  //Lokacijose du operacijų tipai prasideda S raide, kad atskirti sąraše pagal spalvą pakeičiam raidę
  if (
    data &&
    data.operationType &&
    data.operationType.toUpperCase() === "SAR"
  ) {
    opTip = "X";
  }

  const docPos =
    data && data.documentPositions ? parseInt(`${data.documentPositions}`) : 0;
  const appPos =
    data && data.appPositions ? parseInt(`${data.appPositions}`) : 0;

  const handleCloseMenu = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleClick = useCallback(
    (event: any) => {
      if (linkId) {
        navigate(linkTo);
      } else {
        setAnchorEl(anchorEl !== null ? null : event.currentTarget);
      }
    },
    [linkId, linkTo, navigate, anchorEl],
  );

  return (
    <div className={classes.bgColor}>
      {Boolean(anchorEl) && !linkId && (
        <HeaderMenu
          open={Boolean(anchorEl)}
          onClose={handleCloseMenu}
          anchorEl={anchorEl}
          data={data}
          module={module}
          locale={locale}
          clientDbId={userDbId}
          onHeaderUpdate={(hdr) => {
            if (hdr.fileCount !== undefined) {
              data.fileCount = hdr.fileCount; // atnaujinam props lokaliai
            }
          }}
        />
      )}

      <div
        className={cx(classes[headerColor], {
          [classes.expand]: expandRows,
        })}
        onClick={handleClick}
      >
        <div
          className={cx(classes.container, {
            [classes.limitedHeight]: !expandRows,
          })}
        >
          <div
            className={cx(classes.left, {
              [classes.limitedHeight]: !expandRows,
            })}
          >
            <div className={cx({ [classes.noWrap]: !expandRows })}>
              {data && data.element1 ? (
                <div
                  className={classes.lines}
                  dangerouslySetInnerHTML={SimpleMarkdown(data.element1, theme)}
                ></div>
              ) : (
                loading && <Skeleton />
              )}
            </div>

            <div
              className={cx(classes.bottomLeft, {
                [classes.noWrap]: !expandRows,
              })}
            >
              {data && data.element2 ? (
                <div
                  className={classes.lines}
                  dangerouslySetInnerHTML={SimpleMarkdown(data.element2, theme)}
                ></div>
              ) : (
                loading && <Skeleton />
              )}
            </div>
          </div>
          {/* Savaržėlės ikona jei yra prisegtų failų */}
          {Number(data?.fileCount) > 0 && (
            <AttachFile
              sx={{
                color: "rgb(0, 125, 80)",
              }}
            />
          )}

          <div className={classes.right}>
            {/* Viršutinė dešinės pusės eilutė  - operacijos tipas ir atrinktas kiekis */}

            {data && data.operationType && (
              <div className={classes.rightElement}>
                <div>{appPos}</div>

                {(() => {
                  if (opTip === "A") {
                    return <div className="iconA">{opTip}</div>;
                  }
                  if (opTip === "R") {
                    return <div className="iconR">{opTip}</div>;
                  }
                  if (opTip === "L") {
                    return <div className="iconL">{opTip}</div>;
                  }
                  if (opTip === "X") {
                    return <div className="iconX">{opTip}</div>;
                  }
                  if (opTip === "S") {
                    return <div className="iconS">{opTip}</div>;
                  } else {
                    return <div className="iconP">{opTip}</div>;
                  }
                })()}
              </div>
            )}

            {/* Apatinė dešinės pusės eilutė  - Naujas ar jau renkamas ir reikalingas kiekis */}
            <div className={classes.rightElement}>
              <div> {docPos}</div>

              {appState === "K" ? (
                <div className="iconK">{appState}</div>
              ) : (
                <div className="iconN">{appState}</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default HeaderField;
